import { Fragment, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';
import { Button } from '../components/Button';
import { Card, Cards } from '../components/Card';
import { Field } from '../components/Field';
import { Select } from '../components/Select';
import { InfoTip, Tag } from '../components/InfoTip';
import { Menu, MenuItem, useMenu } from '../components/Menu';
import { SideNavShell, activeFromHash } from '../components/SideNav';
import { Modal } from '../components/Modal';
import { Table } from '../components/Table';
import { Tabs } from '../components/Tabs';
import { AdminTabs } from '../pages/admin/AdminTabs';

/**
 * The component gallery — /admin/storybook's counterpart, ported section for section from views/storybook.ejs.
 *
 * It renders the REAL components, not hand-written markup that imitates them: a component that looks right here
 * is one that looks right on a page, because there is one implementation behind both.
 *
 * The page is a shell (`sb-shell`: a sticky sidebar and a panel), ONE section at a time — the hash chooses it, so
 * `/admin/storybook#sb-modal` opens the modal entry and back/forward walk the entries. The nav, the section titles,
 * their order and every preview's copy are the page's: an earlier SPA version invented its own order and titles,
 * made each SECTION a card, and left two tab demos floating above the shell.
 */
const SECTIONS: [string, string][] = [
  ['table', 'Data table'],
  ['tabs', 'Tabs'],
  ['cards', 'Cards'],
  ['field', 'Form field'],
  ['buttons', 'Buttons'],
  ['modal', 'Modal'],
  ['badge', 'Badge'],
  ['tip', 'Info tip'],
  ['panel', 'Panel card'],
  ['menu', 'Menus'],
];

/** The entry the hash names, or the first one when it names none. */
function sectionFrom(hash: string): string {
  return activeFromHash(hash, SECTIONS.map(([k]) => k), 'sb-') || 'table';
}

/** A section's frame: the page's own markup, so its heading and intro read as the page's do. */
function Section({ id, title, intro, hidden, children }: {
  id: string; title: string; intro: ReactNode; hidden: boolean; children: ReactNode;
}) {
  return (
    <section className="sb-section" id={`sb-${id}`} data-sb-section hidden={hidden}>
      <h2 className="t-title" style={{ marginTop: 0 }}>{title}</h2>
      <p className="muted t-small" style={{ marginTop: -4 }}>{intro}</p>
      {children}
    </section>
  );
}

/** The API block a section ends with: the include's own source, as the page prints it. */
function ApiCode({ children }: { children: string }) {
  return (
    <pre className="t-small" style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, overflowX: 'auto', margin: 0 }}>
      <code>{children}</code>
    </pre>
  );
}

/** A preview card inside a section: the page's `panel-card` + `sb-title` + its one-line explanation. */
function Demo({ title, copy, children, id, style }: {
  title: string; copy?: ReactNode; children?: ReactNode; id?: string; style?: CSSProperties;
}) {
  return (
    <div className="panel-card" id={id} style={{ marginBottom: 18, ...style }}>
      <h3 className="sb-title">{title}</h3>
      {copy ? <p className="muted t-small">{copy}</p> : null}
      {children}
    </div>
  );
}


/* ── Menus: five live dropdowns ──────────────────────────────────────────────────────────────
 *
 * Each demo is a real trigger and a real panel, not a picture of one: clicking opens the same component the
 * pages open, and the keyboard contract is the component's (ArrowDown opens onto the first row, arrows walk,
 * Escape closes and returns focus here, Tab closes without trapping, letters jump).
 */

function MenuHeaderDemo() {
  const m = useMenu({ menu: 'sb-menu-header', trigger: 'sb-menu-header-btn' });
  return (
    <>
      <Button {...m.triggerProps} ref={m.triggerRef} variant="ghost" ariaLabel="List actions">⋯</Button>
      <Menu {...m.menuProps} align="left" minWidth={210}>
        <MenuItem className="pane-menu-item" icon={<PencilIcon />} onSelect={() => {}}>Edit list</MenuItem>
        <MenuItem className="pane-menu-item" icon={<ReorderIcon />} onSelect={() => {}}>Reorder tickers</MenuItem>
        <MenuItem className="pane-menu-item" icon={<UploadIcon />} onSelect={() => {}}>Import CSV</MenuItem>
      </Menu>
    </>
  );
}

function MenuRowDemo() {
  const m = useMenu({ menu: 'sb-menu-row', trigger: 'sb-menu-row-btn' });
  return (
    <>
      <Button {...m.triggerProps} ref={m.triggerRef} variant="quiet" size="sm" shape="icon" ariaLabel="Row actions">⋯</Button>
      <Menu {...m.menuProps} align="left" minWidth={196} shadow="0 8px 30px var(--scrim)">
        <MenuItem className="pane-menu-item" icon={<EyeIcon />} onSelect={() => {}}>Show details</MenuItem>
        <MenuItem className="pane-menu-item" icon={<PencilIcon />} onSelect={() => {}}>Edit ticker</MenuItem>
        <MenuItem className="pane-menu-item pane-menu-danger" danger icon={<TrashIcon />} onSelect={() => {}}>Delete ticker</MenuItem>
      </Menu>
    </>
  );
}

function MenuAccountDemo() {
  const m = useMenu({ menu: 'sb-menu-account', trigger: 'sb-menu-account-btn' });
  return (
    <>
      <button {...m.triggerProps} ref={m.triggerRef} type="button" className="t-body"
        style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 999,
          color: 'var(--text)', lineHeight: 1, cursor: 'pointer', padding: '4px 14px 6px',
          display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>you@example.com</span>
        <span style={{ lineHeight: 1, transform: 'translateY(1px)' }}>▾</span>
      </button>
      <Menu {...m.menuProps} align="right" minWidth={140}>
        <div style={{ padding: '4px 0' }}>
          <MenuItem href="#sb-menu" className="um-item" icon={<GearIcon />}>Settings</MenuItem>
          <form method="post" action="#sb-menu" role="none" style={{ paddingTop: 4 }}
            onSubmit={(e) => e.preventDefault()}>
            <MenuItem type="submit" className="um-item danger" icon={<LogoutIcon />}>Log out</MenuItem>
          </form>
        </div>
      </Menu>
    </>
  );
}

function MenuCheckableDemo() {
  const m = useMenu({ menu: 'sb-menu-check', trigger: 'sb-menu-check-btn' });
  const [details, setDetails] = useState(true);
  const [fees, setFees] = useState(false);
  return (
    <>
      <Button {...m.triggerProps} ref={m.triggerRef} variant="ghost" ariaLabel="Ticker actions">⋯</Button>
      <Menu {...m.menuProps} align="left" minWidth={210}>
        <MenuItem className="pane-menu-item" checked={details} closeOnSelect={false}
          onSelect={() => setDetails((v) => !v)}>Show details</MenuItem>
        <MenuItem className="pane-menu-item" checked={fees} closeOnSelect={false}
          onSelect={() => setFees((v) => !v)}>Show fees</MenuItem>
        <MenuItem className="pane-menu-item pane-menu-danger" danger onSelect={() => {}}>Delete ticker</MenuItem>
      </Menu>
    </>
  );
}

function MenuSelectDemo() {
  const [v, setV] = useState('fifo');
  return (
    <Select id="sb-menu-select" value={v} onChange={setV} ariaLabel="Cost basis method"
      options={[{ value: 'average', label: 'Average cost' }, { value: 'fifo', label: 'FIFO' },
        { value: 'lifo', label: 'LIFO' }, { value: 'specific', label: 'Specific lots' }]} />
  );
}

const PencilIcon = () => <span className="menu-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg></span>;
const ReorderIcon = () => <span className="menu-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 4v16M8 20l-3-3M8 20l3-3" /><path d="M16 20V4M16 4l-3 3M16 4l3 3" /></svg></span>;
const UploadIcon = () => <span className="menu-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5-5 5 5" /><path d="M12 5v12" /></svg></span>;
const EyeIcon = () => <span className="menu-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></svg></span>;
const TrashIcon = () => <span className="menu-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></svg></span>;
const GearIcon = () => <span className="um-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg></span>;
const LogoutIcon = () => <span className="um-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg></span>;

export function Gallery() {
  // The hash IS the state, read through the router: a plain `<a href="#sb-cards">` on this page (the link-tab
  // preview is exactly that) is caught by the shell's delegated link handler and pushed through the router, which
  // fires no `hashchange` — a listener would leave the panel on the old entry while the address said otherwise.
  const { hash } = useLocation();
  const navigate = useNavigate();
  // A click sets the entry at once AND pushes the address: the router renders after the hash has already changed,
  // and a panel that catches up a tick later shows the previous component beside the new hash. Back/forward and a
  // fresh load come in through the hash, which is the state.
  const [section, setSection] = useState(() => sectionFrom(hash));
  useEffect(() => { setSection(sectionFrom(hash)); }, [hash]);
  const shown = (key: string) => section === key;

  // The previews' own state (the page's script owns the same for its copies).
  const [panelTab, setPanelTab] = useState('first');
  const [segTab, setSegTab] = useState('day');
  const [rowOpen, setRowOpen] = useState('open');
  const [multiOpen, setMultiOpen] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [fieldSelect, setFieldSelect] = useState('general');

  const go = (key: string) => {
    // flushSync: the page's own handler swaps the panel inside the click, so the DOM already shows the entry the
    // hash names by the time the click returns. Batched, the render lands a tick after the address changed, and
    // anything reading the panel right after the hash (a test, a script) sees the previous component.
    flushSync(() => setSection(key));
    if (hash !== `#sb-${key}`) navigate(`#sb-${key}`);
    // The panel is one component tall: start it at the top rather than keeping the previous one's scroll offset.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  return (
    <main>
      <AdminTabs active="storybook" />

      <SideNavShell
        items={SECTIONS.map(([key, label]) => ({ key, label }))}
        active={section}
        hrefOf={(k) => `#sb-${k}`}
        onSelect={go}
        ariaLabel="Components"
        groupLabel="Components"
      >
          {/* ── Data table ────────────────────────────────────────────────────────────────────────── */}
          <Section id="table" title="Data table" hidden={!shown('table')}
            intro="One component for every table: header, numeric alignment, chevron column, expansion row and pane cell. A page passes columns and rows; it never writes table markup.">
            <Demo id="sb-plain" title="Plain" copy="No row action, so no chevron column and no expansion row.">
              <Table
                expandable={false}
                columns={[{ label: 'Ticker' }, { label: 'Value', num: true }, { label: 'Day', num: true }]}
                rows={[
                  { key: 'a', cells: [<strong key="a">AAPL</strong>, '$1,240.00', '+2.4%'] },
                  { key: 'm', cells: [<strong key="m">MSTY</strong>, '$5,000.00', '\u22120.8%'] },
                ]}
              />
            </Demo>

            <Demo id="sb-expandable" title="Expandable"
              copy="Click a row to expand or collapse it. The chevron column is 40px, the pane spans every column, and the pane cell paints the tint.">
              <Table
                tableClass="holdings-table row-hover"
                onClickRow={(k) => setRowOpen((r) => (r === k ? '' : k))}
                columns={[{ label: 'Month' }, { label: 'Payments', num: true }, { label: 'Total', num: true }]}
                rows={[
                  {
                    key: 'open',
                    rowClass: 'row-action',
                    rowAttrs: { 'data-demo': 'open' },
                    cells: ['2026-09', '2', '$58.00'],
                    pane: {
                      open: rowOpen === 'open',
                      paneClass: 'div-history',
                      rowAttrs: { 'data-demo': 'open' },
                      // the page paints this table into a host div at load, so the pane carries one too
                      content: (
                        <div data-demo-history="">
                          <Table
                            expandable={false}
                            tableClass=""
                            columns={[{ label: 'Date' }, { label: 'Amount', num: true }]}
                            rows={[
                              { key: 'a', cells: ['2026-09-24', '$34.00'] },
                              { key: 'b', cells: ['2026-09-17', '$24.00'] },
                            ]}
                          />
                        </div>
                      ),
                    },
                  },
                  {
                    key: 'closed',
                    rowClass: 'row-action',
                    rowAttrs: { 'data-demo': 'closed' },
                    cells: ['2026-08', '1', '$24.00'],
                    pane: {
                      open: rowOpen === 'closed',
                      paneClass: 'div-history',
                      rowAttrs: { 'data-demo': 'closed' },
                      content: <p className="muted t-body">No payments.</p>,
                    },
                  },
                  {
                    // A cell can carry a second line; the row grows and the chevron stays vertically centred.
                    key: 'two-line',
                    rowClass: 'row-action',
                    rowAttrs: { 'data-demo': 'two-line' },
                    cells: [
                      <Fragment key="m">
                        <strong>2026-07</strong>
                        <div className="muted t-small">Quarterly · 2 payments</div>
                      </Fragment>,
                      '2',
                      '$58.00',
                    ],
                    pane: {
                      open: rowOpen === 'two-line',
                      paneClass: 'div-history',
                      rowAttrs: { 'data-demo': 'two-line' },
                      content: <p className="muted t-body">Quarterly detail.</p>,
                    },
                  },
                ]}
              />
            </Demo>

            <Demo id="sb-multi" title="Expandable — multiple"
              copy={<>The same component with <code>singleExpand: false</code>: open as many rows as you like.</>}>
              <Table
                expand="multi"
                tableClass="holdings-table row-hover"
                onClickRow={(k) => setMultiOpen((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k]))}
                columns={[{ label: 'Month' }, { label: 'Payments', num: true }, { label: 'Total', num: true }]}
                rows={[
                  {
                    key: 'jul',
                    rowClass: 'row-action',
                    cells: ['2026-07', '1', '$24.00'],
                    pane: { open: multiOpen.includes('jul'), paneClass: 'div-history', content: <p className="muted" style={{ padding: '10px 14px', margin: 0 }}>July detail.</p> },
                  },
                  {
                    key: 'jun',
                    rowClass: 'row-action',
                    cells: ['2026-06', '2', '$48.00'],
                    pane: { open: multiOpen.includes('jun'), paneClass: 'div-history', content: <p className="muted" style={{ padding: '10px 14px', margin: 0 }}>June detail.</p> },
                  },
                ]}
              />
            </Demo>

            <Demo id="sb-align" title="Row alignment" style={{ marginBottom: 18 }}
              copy="The quick check for valign faults. In every row the text should sit the same distance from the row's top and bottom borders; in a two-line row each cell's first line should stay on one line and only the wrapping cell should grow downward; and a badge's label should look centred in its own pill. Rows are 34px until a cell wraps.">
              <Table
                expandable={false}
                tableId="sb-align-table"
                tableClass="row-hover"
                columns={[{ label: 'Case' }, { label: 'Cell' }, { label: 'Units', num: true }]}
                rows={[
                  { key: 'one', cells: ['One line', 'Plain text beside a number, as most rows look.', '12'] },
                  {
                    key: 'two',
                    cells: ['Two lines', <div key="w" className="sb-wrap-demo">A cell that wraps: its first line stays where a one-line row's sits and the second hangs below it.</div>, '18'],
                  },
                  { key: 'badge', cells: ['Badge', <Tag key="t">USER</Tag>, '—'] },
                  {
                    key: 'badge-two',
                    cells: ['Badge + two lines', <div key="b" className="sb-wrap-demo"><Tag>ADMIN</Tag> with text that wraps, so both rules show at once.</div>, '24'],
                  },
                ]}
              />
            </Demo>

            <div className="panel-card" id="sb-api">
              <h3 className="sb-title">API</h3>
              <p className="muted t-small">Server-rendered EJS include — no build step.</p>
              <ApiCode>{`<%- include('components/data-table', {
  tableClass: 'holdings-table row-hover',   // optional
  tableId: 'tax-matrix',                    // optional: the table's own id, for page JS
  tableAttrs: { 'data-kind': 'rate' },      // optional: extra attributes on the table
  bodyPartial: { name: 'partials/x', data: {} },  // optional: rows already live in a partial
  expandable: false,                         // optional; false = no chevron/pane
  header: false,                             // optional; false = no <thead> (label/value tables)
  headWrap: true,                            // optional; default keeps headers on one line
  columns: [{ label: 'Month' }, { label: 'Total', num: true }],
  rows: [{
    rowClass: 'row-action',                  // add 'row-action' when the row does something
    attrs: { 'data-ym': '2026-09' },         // copied to the row and its pane
    cells: ['2026-09', '$58.00'],            // HTML; escape user data
    // a cell may also be { html, colspan, className, attrs }
    pane: { paneClass: 'div-history', open: false, html: '...' },
  }],
}) %>`}</ApiCode>
            </div>
          </Section>

          {/* ── Tabs ──────────────────────────────────────────────────────────────────────────────── */}
          <Section id="tabs" title="Tabs" hidden={!shown('tabs')}
            intro="One bar for a tab that swaps a panel and for a link that loads a URL. The component owns the box, the states and the keyboard; the page owns its panel.">
            <Demo title="Panel tabs" copy="Arrow keys move between tabs, Home/End jump to the ends, and only the selected tab is in the Tab order.">
              <Tabs
                id="sb-tabpanel-demo"
                ariaLabel="Gallery panel tabs"
                active={panelTab}
                onChange={setPanelTab}
                items={[
                  { key: 'first', label: 'First', pane: 'sb-tabpanel-1' },
                  { key: 'second', label: 'Second', pane: 'sb-tabpanel-2' },
                  { key: 'third', label: 'Third', pane: 'sb-tabpanel-3' },
                ]}
              />
              <div id="sb-tabpanel-1" role="tabpanel" hidden={panelTab !== 'first'}>
                <p className="t-body" style={{ margin: '8px 0 0' }}>The first panel. Only one panel is visible; the bar sets <span className="api-mono">aria-selected</span> and the panel's <span className="api-mono">role</span> / <span className="api-mono">aria-labelledby</span>.</p>
              </div>
              <div id="sb-tabpanel-2" role="tabpanel" hidden={panelTab !== 'second'}>
                <p className="t-body" style={{ margin: '8px 0 0' }}>The second panel.</p>
              </div>
              <div id="sb-tabpanel-3" role="tabpanel" hidden={panelTab !== 'third'}>
                <p className="t-body" style={{ margin: '8px 0 0' }}>The third panel.</p>
              </div>
            </Demo>

            <div className="panel-card" id="sb-segmented" style={{ marginBottom: 18 }}>
              <h3 className="sb-title">Segmented (alternative look)</h3>
              <p className="muted t-small">The same bar in its <span className="api-mono">segmented</span> variant: a recessed track with the selected tab raised as a thumb, rather than a flat tint. Same component, same ARIA and keyboard — <span className="api-mono">variant: 'segmented'</span> is the only difference. Every bar in the app uses it, all in the raised tone.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
                <Tabs
                  id="sb-segmented-demo"
                  variant="segmented"
                  ariaLabel="Gallery segmented tabs"
                  active={segTab}
                  onChange={setSegTab}
                  items={[
                    { key: 'day', label: 'Day', pane: 'sb-seg-pane-day' },
                    { key: 'week', label: 'Week', pane: 'sb-seg-pane-week' },
                    { key: 'month', label: 'Month', pane: 'sb-seg-pane-month' },
                  ]}
                />
                <Tabs
                  id="sb-segmented-raised-demo"
                  variant="segmented"
                  raised
                  ariaLabel="Gallery segmented tabs, raised"
                  active="week"
                  items={[
                    { key: 'day', label: 'Day', pane: 'sb-seg-raised-day' },
                    { key: 'week', label: 'Week', pane: 'sb-seg-raised-week' },
                    { key: 'month', label: 'Month', pane: 'sb-seg-raised-month' },
                  ]}
                />
              </div>
              <p className="muted t-small" style={{ margin: '8px 0 0' }}>Left is the default tone — the selected segment in <span className="api-mono">--border</span>, the same colour a flat bar's selected tab uses. Right is <span className="api-mono">raised: true</span> — the selected segment in the panel colour (white on the light themes), which is what every bar in the app uses. Both are the same bar; only the paint differs.</p>
              <div id="sb-seg-raised-day" role="tabpanel" style={{ display: 'none' }}><p className="t-body" style={{ margin: '10px 0 0' }}>The day's figures.</p></div>
              <div id="sb-seg-raised-week" role="tabpanel"><p className="t-body" style={{ margin: '10px 0 0' }}>The week's figures.</p></div>
              <div id="sb-seg-raised-month" role="tabpanel" style={{ display: 'none' }}><p className="t-body" style={{ margin: '10px 0 0' }}>The month's figures.</p></div>
              <div id="sb-seg-pane-day" role="tabpanel" hidden={segTab !== 'day'}><p className="t-body" style={{ margin: '10px 0 0' }}>The day's figures.</p></div>
              <div id="sb-seg-pane-week" role="tabpanel" hidden={segTab !== 'week'}><p className="t-body" style={{ margin: '10px 0 0' }}>The week's figures.</p></div>
              <div id="sb-seg-pane-month" role="tabpanel" hidden={segTab !== 'month'}><p className="t-body" style={{ margin: '10px 0 0' }}>The month's figures.</p></div>
            </div>

            <Demo title="Link tabs"
              copy={<>A tab that loads a URL is a link, not a tab: the bar is <span className="api-mono">role="navigation"</span> and the current entry carries <span className="api-mono">aria-current="page"</span> — no <span className="api-mono">aria-selected</span>, because nothing is being switched in place. Its entries are the gallery's own, so this bar behaves like admin's: it marks the page you are on — Tabs, here — and a click really navigates (leaving Tabs, as a link would).</>}>
              <Tabs
                id="sb-tablink-demo"
                ariaLabel="Gallery link tabs"
                // the bar's entries are the gallery's own, so its mark follows the section on show — a chip that
                // stays lit while another component is in the panel is a dead demo
                active={`sb-${section}`}
                items={[
                  { key: 'sb-tabs', label: 'Tabs', href: '#sb-tabs' },
                  { key: 'sb-table', label: 'Data table', href: '#sb-table' },
                  { key: 'sb-cards', label: 'Cards', href: '#sb-cards' },
                  { key: 'sb-field', label: 'Form field', href: '#sb-field' },
                ]}
              />
            </Demo>

            <div className="panel-card">
              <h3 className="sb-title">API</h3>
              <p className="muted t-small">Server-rendered EJS include. A page builds a bar that only exists in JS with <code>PFTabs.render(host, opts)</code> — the same markup and behaviour.</p>
              <ApiCode>{`<%- include('components/tabs', {
  id: 'div-tabs',                       // required for tabs: ids become div-tabs-tab-<key>
  ariaLabel: 'Dividend breakdown',      // the bar's accessible name
  active: 'monthly',
  attrs: { style: 'margin: 4px 0 12px' },  // the page's own margins; the box is the component's
  items: [
    { key: 'monthly', label: 'By Month', pane: 'div-tab-monthly' },  // pane id -> aria-controls
    { key: 'yearly',  label: 'By Year',  pane: 'div-tab-yearly' },
  ],
  trailing: include('partials/home-tabs-end'),  // optional markup after the last tab
}) %>`}</ApiCode>
              <p className="muted t-small" style={{ marginBottom: 0 }}>A page that must react to a switch listens for <code>pf:tab</code> on the bar (it bubbles): <code>e.detail.key</code> is the selected key.</p>
            </div>
          </Section>

          {/* ── Cards ─────────────────────────────────────────────────────────────────────────────── */}
          <Section id="cards" title="Cards" hidden={!shown('cards')}
            intro="The summary row at the top of a page: a label, a figure, and optionally a note under it or an ⓘ beside the title. A page supplies the figures; the card owns the box.">
            <Demo title="A row" copy={<>The row is the component too: <span className="api-mono">components/cards</span> renders one card per item and the grid around them.</>}>
              <Cards>
                <Card label="Total value" value={<span title="$12,400.00">$12.4K</span>} />
                <Card label="Unrealized" valueClass="pos" value="$2,400.00 (24.0%)" />
                <Card label="Day change" valueClass="neg" value="-$180.00 (-1.4%)" />
              </Cards>
            </Demo>

            <Demo title="With a note and a tooltip"
              copy={<>A card can carry a line under its figure (<span className="api-mono">note</span>) and an ⓘ (<span className="api-mono">tip</span>) — both are the page's markup, placed by the card.</>}>
              <Cards>
                <Card
                  label="Tax from investments"
                  style={{ position: 'relative', borderColor: 'var(--accent)' }}
                  value={<span title="$18,200.00">$18.2K</span>}
                  // views/partials/notes/still-due.ejs, markup and tooltip included
                  note={(
                    <div
                      className="muted tax-small"
                      id="card-still-due"
                      title="What the four quarterly payments still ask for this year — a later loss can leave earlier quarters overpaid, in which case this exceeds the year's tax and the excess comes back at filing"
                      style={{ marginTop: 4 }}
                    >
                      Still due <strong className="neg">$4,550.00</strong>
                    </div>
                  )}
                  tip={<InfoTip anchor="card" icon="tax" width={280}>Federal tax on short-term and long-term gains, dividends tax, NIIT, and the state tax the gains themselves add — the same state figure the quarterly payments charge.</InfoTip>}
                />
                <Card label="Total Tax" valueTag="strong" value="$22,750.00" />
              </Cards>
            </Demo>

            <div className="panel-card">
              <h3 className="sb-title">API</h3>
              <p className="muted t-small">Server-rendered EJS include, one per row of cards. <span className="api-mono">valueTag: 'strong'</span> matches the tax panels' heavier figure.</p>
              <ApiCode>{`<%- include('components/cards', {
  className: 'hv-cards',             // optional: a row variant
  style: 'margin-top: 12px',         // the row's own geometry
  items: [{
    label: 'Total value',            // HTML: lbl() output keeps its full/short spans
    value: '<span title="$12,400">$12.4K</span>',
    valueClass: 'pos',               // pos / neg when the sign means something
    valueId: 'm-rss',                // a figure the page updates in place
    valueTag: 'strong',              // default div; the tax panels use strong
    title: 'plain-text tooltip',     // escaped
    note: include('partials/notes/still-due', { amount: 4550 }),  // a rendered partial under the figure
    tip: include('partials/tips/tax-total'),      // the ⓘ, if the card has one
    style: 'position: relative; border-color: var(--accent);',
  }],
}) %>`}</ApiCode>
            </div>
          </Section>

          {/* ── Form field ────────────────────────────────────────────────────────────────────────── */}
          <Section id="field" title="Form field" hidden={!shown('field')}
            intro="A label, its control, and an optional hint or error, as one column. The field owns the label's type, the control's width and the 38px control height — a page supplies the control and nothing else.">
            <Demo title="A field" copy={<>Label above control, stacked at the app's 4px gap. Clicking the label focuses the control (<span className="api-mono">for</span>).</>}>
              <div className="sb-demo">
                <Field label="Ticker symbol" htmlFor="sb-field-text" required>
                  <input id="sb-field-text" placeholder="AAPL" />
                </Field>
              </div>
            </Demo>

            <Demo title="With a hint, and with an error"
              copy={<>A <span className="api-mono">hint</span> sits under the control in the muted small type; an <span className="api-mono">error</span> takes the same place in the red.</>}>
              <div className="sb-demo">
                <Field label="Grant date" htmlFor="sb-field-hint" hint="Blank means the expiration is ten years after the grant.">
                  <input id="sb-field-hint" type="date" />
                </Field>
                <Field label="Shares" htmlFor="sb-field-error" error="Enter a number of shares above zero.">
                  <input id="sb-field-error" type="number" aria-invalid="true" />
                </Field>
              </div>
            </Demo>

            <Demo title="A control that shares its row"
              copy={<>A select and its hint sit in a <span className="api-mono">field-row</span>: the control takes the space the hint leaves, and both stay inside the field's column.</>}>
              <div className="sb-demo">
                <Field label="Type" htmlFor="sb-field-select">
                  <div className="field-row">
                    {/* the page writes a native <select>; views/layout.ejs decorates it into .sel > .sel-btn at
                        load, so the SPA's port of that decoration is what a reader (and the tests) see */}
                    <Select
                      id="sb-field-select"
                      value={fieldSelect}
                      onChange={setFieldSelect}
                      ariaLabel="Type"
                      options={[
                        { value: 'general', label: 'General' },
                        { value: 'watchlist', label: 'Watchlist' },
                        { value: 'tax', label: 'Tax-deferred' },
                      ]}
                    />
                    <span className="t-small muted" style={{ whiteSpace: 'nowrap' }}>Excluded from totals</span>
                  </div>
                </Field>
              </div>
            </Demo>

            <div className="panel-card">
              <h3 className="sb-title">API</h3>
              <p className="muted t-small">Server-rendered EJS include. The control is markup, so a select, a textarea or a row of them all fit; <span className="api-mono">labelHtml</span> and <span className="api-mono">hint</span> take markup too.</p>
              <ApiCode>{`<%- include('components/form-field', {
  label: 'Ticker symbol',        // the caption (labelHtml for markup)
  for: 'at_symbol',              // the control's id, so the label focuses it
  required: true,                // adds the red asterisk
  hint: '…',                     // a muted line under the control
  error: '…',                    // takes the hint's place in red
  control: '<input id="at_symbol" name="ticker" required>',
}) %>`}</ApiCode>
            </div>
          </Section>

          {/* ── Buttons ───────────────────────────────────────────────────────────────────────────── */}
          <Section id="buttons" title="Buttons" hidden={!shown('buttons')}
            intro={<>One control, with the geometry owned by <span className="api-mono">components/button</span>: a page picks a variant, a size and a shape and never sets padding, height or width itself.</>}>
            <Demo title="Variants"
              copy={<><span className="api-mono">primary</span> (the default) is the one action in a view; <span className="api-mono">secondary</span> sits beside it; <span className="api-mono">ghost</span> is a bordered quiet action; <span className="api-mono">quiet</span> is text until hover; <span className="api-mono">danger</span> is the destructive one, in the theme's red.</>}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button>Save</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="quiet">Quiet</Button>
                <Button variant="danger">Delete</Button>
                <Button disabled>Disabled</Button>
              </div>
            </Demo>

            <Demo title="Sizes" copy={<><span className="api-mono">md</span> is 35px (the default), <span className="api-mono">sm</span> 28px, <span className="api-mono">xs</span> 22px — three heights, no fourth.</>}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="ghost">Medium</Button>
                <Button variant="ghost" size="sm">Small</Button>
                <Button variant="ghost" size="xs">Extra small</Button>
              </div>
            </Demo>

            <Demo title="Shapes" copy={<>An <span className="api-mono">icon</span> button is square with a centred glyph; a <span className="api-mono">menu-row</span> fills its menu, left-aligns its caption and puts the icon in one 20px column so every row's label starts on the same x.</>}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                <Button variant="quiet" shape="icon" ariaLabel="Add">+</Button>
                <Button variant="ghost" shape="icon" ariaLabel="Add" icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>} />
                <Button variant="secondary" block>A block button</Button>
              </div>
              <div className="sb-demo" style={{ maxWidth: 260, border: '1px solid var(--border)', borderRadius: 8, padding: '4px 0' }}>
                <Button shape="menu-row" className="t-dense" icon={<span className="menu-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg></span>}>Edit list</Button>
                <Button shape="menu-row" className="t-dense" variant="danger" icon={<span className="menu-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" /></svg></span>}>Delete list</Button>
              </div>
            </Demo>

            <div className="panel-card">
              <h3 className="sb-title">API</h3>
              <p className="muted t-small">Server-rendered EJS include. <span className="api-mono">style</span> is for layout the component has no size for (a margin, <span className="api-mono">display:none</span>) — never padding, height or width.</p>
              <ApiCode>{`<%- include('components/button', {
  label: 'Save',                  // or labelHtml for markup
  variant: 'primary',             // primary | secondary | ghost | quiet | danger
  size: 'md',                     // md 35px | sm 28px | xs 22px
  shape: 'text',                  // text | icon | menu-row
  icon: '<svg …>',               // before the caption (menu rows, icon buttons)
  type: 'button',                 // button | submit | reset
  block: false,                   // full width
  className: 'js-copy',           // classes the caller owns
  attrs: { id: 'save', title: 'Save & close' },
  disabled: false,
}) %>`}</ApiCode>
            </div>
          </Section>

          {/* ── Modal ─────────────────────────────────────────────────────────────────────────────── */}
          <Section id="modal" title="Modal" hidden={!shown('modal')}
            intro="One dialog chrome for every modal: overlay, box, title, ✕ and the open/close behaviour (Escape, backdrop, focus). A page passes a title, a size and its body — it never writes the overlay or the close wiring.">
            <Demo title="Preview" copy={<>Opens through PFModal. Try Escape, a click on the dark backdrop, or ✕ — all three close it, and focus returns to this button.</>}>
              {/* the page's button carries no id: the dialog is opened by `data-modal-open` */}
              <button type="button" className="btn" data-modal-open="sb-modal-demo" onClick={() => setModalOpen(true)}>Open a modal</button>
            </Demo>

            <div className="panel-card">
              <h3 className="sb-title">API</h3>
              <p className="muted t-small">Server-rendered EJS include; the body is its own partial. Sizes: <code>sm</code> 400, <code>md</code> 560, <code>lg</code> 720, <code>xl</code> 860.</p>
              <ApiCode>{`<%- include('components/modal', {
  id: 'conn-modal',                 // required: the overlay id; title/✕ become conn-modal-title / -x
  title: 'Connect a brokerage',     // escaped; titleHtml when the heading carries markup
  subtitle: 'One line under it',    // optional, accepts markup
  size: 'md',                       // sm | md (default) | lg | xl
  tone: 'danger',                   // optional: red heading (a destructive dialog)
  attrs: { 'data-modal-autoopen': 1 },  // optional: opens on load (a rejected form re-render)
  body: include('partials/modals/conn-body'),
}) %>`}</ApiCode>
              <p className="muted t-small" style={{ marginBottom: 0 }}>Open from script with <code>PFModal.open('conn-modal', {'{ trigger, focus }'})</code>; <code>data-modal-close</code> on any control inside closes it, and <code>data-modal-open="&lt;id&gt;"</code> opens one declaratively.</p>
            </div>

            <Modal
              id="sb-modal-demo"
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              title="Gallery dialog"
              subtitle="The same component every page uses — nothing gallery-specific in here."
              size="md"
            >
              <p className="t-body" style={{ margin: '0 0 12px' }}>A dialog's body is ordinary view markup: fields, tables, steps. The chrome above it, and the way it closes, is the component's.</p>
              <div className="modal-actions">
                <button type="button" className="ghost" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="button" onClick={() => setModalOpen(false)}>Save</button>
              </div>
            </Modal>
          </Section>

          {/* ── Badge ─────────────────────────────────────────────────────────────────────────────── */}
          <Section id="badge" title="Badge" hidden={!shown('badge')}
            intro={<>The <span className="api-mono">.tag</span> chip: a list's type, a role, a <em>closed</em> or <em>unpriced</em> marker. It is 14px — the row's size, not a heading's — 20px tall, centred by its own box, and in a cell it sits on the row's text baseline so its label lines up with the text beside it.</>}>
            <Demo title="The chips" copy={<>Same class everywhere; a role tag tints it with the accent instead of adding a variant.</>}>
              <div className="sb-demo" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="tag">general</span>
                <span className="tag">watchlist</span>
                <span className="tag">tax deferred</span>
                <span className="tag">closed</span>
                <span className="tag">unpriced</span>
                <span className="tag" style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}>ADMIN</span>
                <span className="tag">USER</span>
              </div>
            </Demo>

            <Demo title="In a table cell"
              copy={<>This is the one that bites: a 20px chip in a 16.5px line box grows the row unless the cell gives it a negative vertical margin — the row below stays 33px because <span className="api-mono">td .tag</span> does.</>}>
              <Table
                expandable={false}
                tableId="sb-badge-table"
                columns={[{ label: 'List' }, { label: 'Type' }, { label: 'Symbols', num: true }]}
                rows={[
                  { key: 'r', cells: ['Retirement', <span key="t" className="tag">tax deferred</span>, '4'] },
                  { key: 'w', cells: ['Watch', <span key="t" className="tag">watchlist</span>, '12'] },
                  { key: 'o', cells: ['Old', <span key="t"><span className="tag">closed</span> <span className="tag">unpriced</span></span>, '2'] },
                ]}
              />
            </Demo>

            <div className="panel-card">
              <h3 className="sb-title">API</h3>
              <p className="muted t-small">Plain markup — the chip has no component, because it is one class on a span.</p>
              <ApiCode>{`<span class="tag">tax deferred</span>
<span class="tag" style="color: var(--accent); border-color: var(--accent);">ADMIN</span>`}</ApiCode>
            </div>
          </Section>

          {/* ── Info tip ──────────────────────────────────────────────────────────────────────────── */}
          <Section id="tip" title="Info tip" hidden={!shown('tip')}
            intro={<>The <span className="api-mono">.info-tip</span> ⓘ: a card's footnote, opened on hover or focus. The page owns the words; the partial owns the chip and the pod, and the card becomes the chip's containing block by itself (<span className="api-mono">.card:has(.info-tip)</span>) — a page supplies nothing but its content.</>}>
            <Demo title="A real one, in a card corner"
              copy={<>This is <span className="api-mono">partials/tips/tax-total</span> itself, not a copy: hover the ⓘ (or Tab to it).</>}>
              <div className="sb-demo">
                <Cards style={{ margin: 0, maxWidth: 420 }}>
                  <Card
                    label="Total tax"
                    value="$12,480"
                    tip={<InfoTip anchor="card" icon="tax" width={280}>All estimated taxes combined: federal ordinary income tax (wages + bonus), capital gains, dividends, NIIT, and state tax.</InfoTip>}
                  />
                </Cards>
              </div>
            </Demo>

            <div className="panel-card">
              <h3 className="sb-title">API</h3>
              <p className="muted t-small">One partial per tip under <span className="api-mono">views/partials/tips/</span> (and <span className="api-mono">notes/</span> for the ones under a card). A page includes it inside a positioned card and writes nothing of its own.</p>
              <ApiCode>{`<%- include('partials/tips/tax-total') %>

<span class="muted">// the chip: 15px circle, italic i, absolute in the card's corner</span>
<span class="info-tip" tabindex="0" role="note">i
  <span class="info-tip-content">…the sentence…</span>
</span>`}</ApiCode>
            </div>
          </Section>

          {/* ── Panel card ────────────────────────────────────────────────────────────────────────── */}
          <Section id="panel" title="Panel card" hidden={!shown('panel')}
            intro={<>The <span className="api-mono">.panel-card</span> box a view puts under its tabs: one surface, 10px radius, 1px border, 20/22/24 padding. A view supplies the heading and the contents; the box supplies the chrome.</>}>
            <Demo title="The box" copy={<>Heading first — the card pulls the first heading's top margin in, so the title sits 4px under the border rather than floating.</>}>
              <div className="sb-demo">
                <div className="panel-card" style={{ marginTop: 0 }}>
                  <h2 className="t-body" style={{ fontWeight: 600, margin: '0 0 6px' }}>Recent transactions</h2>
                  <p className="muted t-small" style={{ margin: 0 }}>Body content goes here: a table, a form, a chart. The card is the surface, not the shape of what's on it.</p>
                </div>
              </div>
            </Demo>

            <div className="panel-card">
              <h3 className="sb-title">API</h3>
              <ApiCode>{`<section class="panel-card">
  <h2 class="t-body">Recent transactions</h2>
  <%- include('components/data-table', { … }) %>
</section>`}</ApiCode>
            </div>
          </Section>

          {/* ── Menus ─────────────────────────────────────────────────────────────────────────────── */}
          <Section id="menu" title="Menus" hidden={!shown('menu')}
            intro={<>One component opens every dropdown: a fixed, portalled panel that places itself below its
              trigger (above, when there is no room), closes on Escape or a click outside, and carries the keyboard
              contract — <span className="api-mono">↓</span> opens onto the first row and walks, <span className="api-mono">Home</span>/<span className="api-mono">End</span> jump,
              a letter jumps to the row that starts with it, <span className="api-mono">Esc</span> closes and returns focus to the trigger.
              Rows are <span className="api-mono">MenuItem</span>s; their look is the class you give them.</>}>
            <div className="panel-card" style={{ marginBottom: 18 }}>
              <h3 className="sb-title">Page-header menu</h3>
              <p className="muted t-small">The <span className="api-mono">⋯</span> beside a page title: a bordered ghost trigger, rows with a 20px icon column and a caption. A page header holds three or four actions at most — beyond that the actions belong on the page.</p>
              <div className="sb-demo" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className="t-small muted">My Lists</span>
                <MenuHeaderDemo />
              </div>
            </div>

            <div className="panel-card" style={{ marginBottom: 18 }}>
              <h3 className="sb-title">Row menu</h3>
              <p className="muted t-small">The same panel from a row: a borderless quiet <span className="api-mono">⋯</span>, narrower, with its own shadow. Choosing a row acts once and closes the menu; a destructive row is red text (<span className="api-mono">danger</span>), never a filled button, and it asks before it acts.</p>
              <div className="sb-demo" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className="t-small muted">Detail pane</span>
                <MenuRowDemo />
              </div>
            </div>

            <div className="panel-card" style={{ marginBottom: 18 }}>
              <h3 className="sb-title">Account menu</h3>
              <p className="muted t-small">The header's own menu: one icon column, one row height, no divider (two actions, not two sections), and a real <span className="api-mono">&lt;form&gt;</span> for the row that signs out (a form inside <span className="api-mono">role="menu"</span> is not a menu row, so it is wrapped <span className="api-mono">role="none"</span>).</p>
              <div className="sb-demo" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <MenuAccountDemo />
              </div>
            </div>

            <div className="panel-card" style={{ marginBottom: 18 }}>
              <h3 className="sb-title">Checkable rows</h3>
              <p className="muted t-small">A row that is a switch, not an action: <span className="api-mono">checked</span> makes it a <span className="api-mono">menuitemcheckbox</span> with <span className="api-mono">aria-checked</span>, and <span className="api-mono">closeOnSelect={'{false}'}</span> keeps the menu open so two boxes can be ticked in one visit.</p>
              <div className="sb-demo" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className="t-small muted">Holding pane</span>
                <MenuCheckableDemo />
              </div>
            </div>

            <div className="panel-card">
              <h3 className="sb-title">Select list</h3>
              <p className="muted t-small">Not a menu: <span className="api-mono">Select</span> is a form control — a native <span className="api-mono">&lt;select&gt;</span> kept for the value and the screen reader, decorated into <span className="api-mono">.sel-btn</span> + a <span className="api-mono">listbox</span> of <span className="api-mono">.sel-opt</span> rows. It opens the same way and closes the same way, but its rows are options, not actions.</p>
              <div className="sb-demo" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className="t-small muted">Cost basis</span>
                <MenuSelectDemo />
              </div>
            </div>

            <ApiCode>{`const m = useMenu();               // aria-haspopup/expanded/controls, ArrowDown opens
<Button {...m.triggerProps} ref={m.triggerRef} variant="ghost">⋯</Button>
<Menu {...m.menuProps} id="list-menu" align="right" minWidth={210}>
  <MenuItem icon={<PencilIcon />} onSelect={edit}>Edit list</MenuItem>
  <MenuItem icon={<TrashIcon />} danger checked={false}>Delete ticker</MenuItem>
</Menu>`}</ApiCode>
          </Section>
      </SideNavShell>
    </main>
  );
}
